import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { CasesSystemToggler } from "@/components/doc/right-sidebar/active-node-style/group/CasesSystemToggler";
import { GroupActions } from "@/components/doc/right-sidebar/active-node-style/group/GroupActions";
import { GroupBracketsSelector } from "@/components/doc/right-sidebar/active-node-style/group/GroupBracketsSelector";
import { MatrixDimensions } from "@/components/doc/right-sidebar/active-node-style/group/MatrixDimensions";
import { DecoratorsMenu } from "@/components/doc/right-sidebar/active-node-style/variable/DecoratorsMenu";
import { hasGroupBounds, hasVerticalVectorBounds } from "@/components/expression/utils/matrix";
import { Sidebar } from "@/components/sidebar";

import { UngroupButton } from "./UngroupButton";

import type { GroupNodeModel } from "@/models/nodes/GroupNodeModel";

type Props = {
  node: GroupNodeModel;
};

export const GroupStyle = observer(function GroupStyle({ node }: Props) {
  const t = useTranslations("sidebar.activeNode.group");
  const { casesSystemEnabled, matrixNode, hasWrappingDecorators } = node;

  const showCasesToggler =
    matrixNode &&
    node.openingSymbol === "{" &&
    (hasGroupBounds(matrixNode.bounds) || hasVerticalVectorBounds(matrixNode.bounds));

  const showUngroupButton =
    matrixNode && hasGroupBounds(matrixNode.bounds) && !node.hasWrappingDecorators;

  return (
    <div data-panel="group_style" className="flex flex-col w-full px-[5px]">
      {matrixNode && (
        <div className="flex justify-between mb-2">
          <MatrixDimensions
            node={matrixNode}
            rowsInputLabel={casesSystemEnabled ? t("actions.addLines") : undefined}
          />
          {showUngroupButton && <UngroupButton node={node} />}
          {!casesSystemEnabled && <DecoratorsMenu node={node} />}
        </div>
      )}
      {!casesSystemEnabled && <GroupActions node={node} />}
      {!casesSystemEnabled && !hasWrappingDecorators && (
        <>
          <Sidebar.Delimiter className="mt-2 mb-2.5" />
          <GroupBracketsSelector node={node} />
        </>
      )}
      {showCasesToggler && (
        <>
          <Sidebar.Delimiter className="mt-1 mb-2.5" />
          <CasesSystemToggler node={node} />
        </>
      )}
    </div>
  );
});
